import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { gallery } from "../../data/gallery";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { ImageSlot } from "../shared/ImageSlot";

export function LightboxGallery({ limit }: { limit?: number }) {
  const items = limit ? gallery.slice(0, limit) : gallery;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const isOpen = activeIndex !== null;

  const close = useCallback(() => {
    setActiveIndex(null);
    window.requestAnimationFrame(() => returnFocusRef.current?.focus());
  }, []);

  useBodyScrollLock(isOpen);
  useEscapeKey(close, isOpen);

  const goPrevious = useCallback(() => {
    setActiveIndex((current) => current === null ? null : (current - 1 + items.length) % items.length);
  }, [items.length]);
  const goNext = useCallback(() => {
    setActiveIndex((current) => current === null ? null : (current + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled])");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [goNext, goPrevious, isOpen]);

  return (
    <>
      <div className="gallery-grid">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className="gallery-card reveal"
            aria-label={`Ampliar: ${item.caption}`}
            onClick={(event) => {
              returnFocusRef.current = event.currentTarget;
              setActiveIndex(index);
            }}
          >
            <ImageSlot slot={item.slot} alt={item.alt} aspect={index % 3 === 0 ? "portrait" : "square"} />
            <span>{item.caption}</span>
          </button>
        ))}
      </div>

      {activeIndex !== null && (
        <div className="lightbox" role="presentation" onMouseDown={close}>
          <div
            ref={dialogRef}
            className="lightbox__dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`Galeria: ${items[activeIndex].caption}`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button ref={closeButtonRef} type="button" className="lightbox__close" onClick={close} aria-label="Fechar galeria">
              <X aria-hidden="true" />
            </button>
            <button type="button" className="lightbox__nav lightbox__nav--prev" onClick={goPrevious} aria-label="Fotografia anterior">
              <ChevronLeft aria-hidden="true" />
            </button>
            <ImageSlot slot={items[activeIndex].slot} alt={items[activeIndex].alt} aspect="wide" />
            <button type="button" className="lightbox__nav lightbox__nav--next" onClick={goNext} aria-label="Fotografia seguinte">
              <ChevronRight aria-hidden="true" />
            </button>
            <p>{items[activeIndex].caption} <span>{activeIndex + 1} / {items.length}</span></p>
          </div>
        </div>
      )}
    </>
  );
}
